import { Globe, LogOut, Menu, Moon, Sun, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LOCALES, useT } from '../../i18n';
import { useApi } from '../../api';
import { useAuthStore, useCurrentMembership } from '../../auth/store';
import { Dropdown, IconButton } from '../../ui';
import { useThemeStore } from '../theme';
import { GlobalSearch } from './GlobalSearch';
import s from './Topbar.module.css';

export function Topbar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { t, locale, setLocale } = useT();
  const theme = useThemeStore((x) => x.theme);
  const toggleTheme = useThemeStore((x) => x.toggle);
  const user = useAuthStore((x) => x.user);
  const clear = useAuthStore((x) => x.clear);
  const membership = useCurrentMembership();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      clear();
      qc.clear();
      navigate('/login', { replace: true });
    }
  };

  const initials = (user?.name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className={s.topbar}>
      <span className={s.menuBtn}>
        <IconButton label={t('shell.openMenu')} icon={<Menu size={20} />} onClick={onOpenMenu} />
      </span>
      <GlobalSearch />
      <div className={s.actions}>
        <Dropdown
          items={LOCALES.map((l) => ({ id: l.code, label: l.label, onSelect: () => setLocale(l.code), icon: <span className={s.check}>{l.code === locale ? '✓' : ''}</span> }))}
          trigger={(props) => <IconButton label={t('shell.language')} icon={<Globe size={20} />} {...props} />}
        />
        <IconButton
          label={theme === 'dark' ? t('shell.themeLight') : t('shell.themeDark')}
          icon={theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          onClick={toggleTheme}
        />
        <Dropdown
          header={
            <div className={s.who}>
              <strong>{user?.name}</strong>
              <span>{user?.email}</span>
              {membership && <span className={s.roleTag}>{membership.roleName}</span>}
            </div>
          }
          items={[
            { id: 'profile', label: t('shell.profile'), icon: <UserRound size={16} />, onSelect: () => navigate('/profile') },
            { id: 'logout', label: t('shell.logout'), icon: <LogOut size={16} />, onSelect: logout, separatorBefore: true, danger: true },
          ]}
          trigger={(props) => (
            <button type="button" className={s.avatar} aria-label={t('shell.userMenu')} {...props}>
              {initials}
            </button>
          )}
        />
      </div>
    </header>
  );
}
